import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface KPIData {
  category: string;
  name: string;
  description: string;
  value: number | null;       // null means dealer isn't reporting this KPI/period type
  type?: string;              // Time period type: "Monthly Values", "Year to Date", etc.
  abbreviation?: string;      // Short form: "MTD", "YTD", "R3", etc.
  typeDescription?: string;   // Full description of the time period
}

export interface SubmittedFinancialData {
  id?: number;
  userId: number;
  organizationId: number;
  locationId?: number;
  year: number;
  month: number;
  kpiData: KPIData[];
  dataQualityScore?: number;
  dataQualityReport?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive: boolean;
}

// Lite version without kpi_data - for fast listing
export interface SubmittedFinancialDataLite {
  id?: number;
  userId: number;
  organizationId: number;
  locationId?: number;
  year: number;
  month: number;
  dataQualityScore?: number;
  dataQualityReport?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive: boolean;
  // Summary stats instead of full kpi_data
  kpiCount: number;
  uniqueKpiCount: number;
  periodTypes: string[];
}

export interface CreateFinancialSubmissionRequest {
  user_id: number;
  organization_id: number;
  location_id?: number;
  year: number;
  month: number;
  kpi_data: KPIData[];
  data_quality_score?: number;
  data_quality_report?: string;
}

class FinancialSubmissionService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async createFinancialSubmission(submission: CreateFinancialSubmissionRequest): Promise<{success: boolean, data?: SubmittedFinancialData, message: string}> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/financial-submissions`,
        submission,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating financial submission:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Network error occurred while creating financial submission'
      };
    }
  }

  async getFinancialSubmissions(): Promise<SubmittedFinancialData[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/financial-submissions`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : [];
    } catch (error: any) {
      console.error('Error fetching financial submissions:', error);
      return [];
    }
  }

  // Lite version - fetches metadata only (no kpi_data) for fast listing
  async getFinancialSubmissionsLite(): Promise<SubmittedFinancialDataLite[]> {
    try {
      const headers = this.getAuthHeaders();
      console.log('📡 Calling lite endpoint with auth:', headers.Authorization ? 'Bearer token present' : '❌ NO TOKEN');
      
      const response = await axios.get(
        `${API_BASE_URL}/financial-submissions?lite=true`,
        { headers }
      );
      return response.data.success ? response.data.data : [];
    } catch (error: any) {
      console.error('Error fetching financial submissions (lite):', error);
      console.error('Status:', error.response?.status, 'Message:', error.response?.data?.message || error.message);
      
      // If 401, fall back to regular endpoint
      if (error.response?.status === 401) {
        console.warn('⚠️ Lite endpoint returned 401, check if token is valid');
      }
      return [];
    }
  }

  async getFinancialSubmissionById(id: number): Promise<SubmittedFinancialData | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/financial-submissions/${id}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.success ? response.data.data : null;
    } catch (error: any) {
      console.error('Error fetching financial submission:', error);
      return null;
    }
  }



  async updateFinancialSubmission(
    id: number, 
    updates: Partial<Pick<CreateFinancialSubmissionRequest, 'kpi_data' | 'data_quality_score' | 'data_quality_report'>>
  ): Promise<SubmittedFinancialData> {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/financial-submissions/${id}`,
        updates,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error('Error updating financial submission:', error);
      throw new Error(error.response?.data?.message || 'Failed to update financial submission');
    }
  }

  async deleteFinancialSubmission(id: number): Promise<{success: boolean, message: string}> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/financial-submissions/${id}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error deleting financial submission:', error);
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        message: 'Network error occurred while deleting financial submission'
      };
    }
  }
}

const financialSubmissionService = new FinancialSubmissionService();
export default financialSubmissionService;
